import {
  useMutation,
  useQuery,
  type QueryKey,
  type UseQueryOptions
} from "react-query";
import {
  qkAppSettingsFromDB,
  qkAppSettingsPermissionList,
  appSettingsQueryKey,
  qkappSettingsSchema
} from "../constants/appSettings.constants";
import {
  getAppSettings,
  getAppSettingsDB,
  getAppSettingsSchema,
  getPermissionsList,
  setAppSettings,
  uploadPlatformSettingsFiles
} from "../services/appSettings.services";
import type {
  AppSettings,
  AppSettingsJSONSchema
} from "../types/appSettings.types";
import { queryClient } from "../utils/utils";
import type {
  UseMutationExtraConfigType,
  UseQueryReturnType
} from "@certa/common/types";

export const useAppSettingsQuery = (
  savepointId: number,
  extraConfig?: UseQueryOptions<AppSettings, Response>
) =>
  useQuery({
    retry: false,
    cacheTime: Infinity,
    staleTime: Infinity,
    ...extraConfig,
    queryKey: [appSettingsQueryKey, savepointId] as QueryKey,
    queryFn: () => getAppSettings(savepointId)
  });

export const useSaveAppSettings = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(setAppSettings, {
    ...extraConfig,
    onSuccess: (data, { appSettings, savepointId }, context) => {
      queryClient.setQueryData([appSettingsQueryKey, savepointId], appSettings);
      extraConfig?.onSuccess?.(data, { appSettings, savepointId }, context);
    }
  });

export const useAppSettingsFromDB = (
  extraConfig?: UseQueryOptions<UseQueryReturnType<typeof getAppSettingsDB>>
) =>
  useQuery({
    // 5min
    staleTime: 1000 * 60 * 5,
    cacheTime: Infinity,
    retry: false,
    ...extraConfig,
    queryKey: qkAppSettingsFromDB as QueryKey,
    queryFn: getAppSettingsDB
  });

export const useAppSettingsFromDBMutation = (
  extraConfig?: UseMutationExtraConfigType
) =>
  useMutation<AppSettings>(getAppSettingsDB, {
    ...extraConfig,
    onSuccess: liveAppSettings => {
      queryClient.setQueryData([qkAppSettingsFromDB], liveAppSettings);
    }
  });

export const invalidateAppSettingsFromDB = () => {
  queryClient.invalidateQueries([qkAppSettingsFromDB]);
};

export const useAppSettingsMutation = (
  extraConfig?: UseMutationExtraConfigType
) =>
  useMutation(getAppSettings, {
    ...extraConfig,
    onSuccess: (appSettings, savepointId) => {
      queryClient.setQueryData([appSettingsQueryKey, savepointId], appSettings);
    }
  });

export const useAppSettingPermissionsList = () =>
  useQuery({
    // 1 hour
    staleTime: 1000 * 60 * 60 * 1,
    cacheTime: Infinity,
    retry: false,
    queryKey: qkAppSettingsPermissionList,
    queryFn: getPermissionsList
  });

export const useUploadPlatformSettingsFile = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(uploadPlatformSettingsFiles, extraConfig);

export const useAppSettingsSchema = () =>
  useQuery({
    // 1 hour
    staleTime: 1000 * 60 * 60 * 1,
    cacheTime: Infinity,
    retry: false,
    queryKey: qkappSettingsSchema,
    queryFn: getAppSettingsSchema
  });

export const getAppSettingsFromQC = async (savepointId: number) => {
  await queryClient.prefetchQuery(
    [appSettingsQueryKey, savepointId],
    () => getAppSettings(savepointId),
    {
      staleTime: Infinity,
      cacheTime: Infinity
    }
  );

  return queryClient.getQueryData<AppSettings>([
    appSettingsQueryKey,
    savepointId
  ]);
};

export const getAppSettingsFromDBFromQC = async () => {
  await queryClient.prefetchQuery(
    [qkAppSettingsFromDB],
    () => getAppSettingsDB(),
    {
      staleTime: Infinity,
      cacheTime: Infinity
    }
  );

  return queryClient.getQueryData<AppSettings>([qkAppSettingsFromDB]);
};

export const getAppSettingsSchemaFromQC = async () => {
  await queryClient.prefetchQuery(
    [qkappSettingsSchema],
    () => getAppSettingsSchema(),
    {
      staleTime: Infinity,
      cacheTime: Infinity
    }
  );

  return queryClient.getQueryData<AppSettingsJSONSchema>([qkappSettingsSchema]);
};

export const updateAppSettings = async (
  appSettings: AppSettings,
  savepointId: number
) => {
  try {
    await setAppSettings({ appSettings, savepointId });
    queryClient.setQueryData([appSettingsQueryKey, savepointId], appSettings);
  } catch (error) {
    throw error;
  }
};
